/**
 * EXP-01B — Explore Real Events
 *
 * Public gallery page. No authentication required.
 * Visual direction: dark premium hero → warm ivory editorial gallery → dark CTA band.
 *
 * Route: /explore
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PJ } from "@/journey/journeyConfig";
import { loadJourneyFromStorage, saveJourneyToStorage, addJourneyPathEntry } from "@/journey/journeyEngine";
import { mapInspirationToJourneyFields } from "../utils/inspirationMapper";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Type / filter configuration ───────────────────────────────────────────────

const EVENT_TYPE_FILTERS = [
  { value: "",           label: "All Events" },
  { value: "WEDDING",    label: "Wedding" },
  { value: "SANGEET",    label: "Sangeet" },
  { value: "ENGAGEMENT", label: "Engagement" },
  { value: "BIRTHDAY",   label: "Birthday" },
  { value: "CORPORATE",  label: "Corporate" },
  { value: "CONFERENCE", label: "Conference" },
  { value: "AWARDS",     label: "Awards" },
];

const GUEST_SCALE_FILTERS = [
  { value: "",         label: "Any Scale" },
  { value: "50_150",   label: "Intimate (50–150)" },
  { value: "150_300",  label: "Medium (150–300)" },
  { value: "300_500",  label: "Large (300–500)" },
  { value: "500_750",  label: "Grand (500–750)" },
  { value: "750_PLUS", label: "Mega (750+)" },
];

const STYLE_FILTERS = [
  { value: "",            label: "Any Style" },
  { value: "Elegant",     label: "Elegant" },
  { value: "Traditional", label: "Traditional" },
  { value: "Modern",      label: "Modern" },
  { value: "Royal",       label: "Royal" },
  { value: "Vibrant",     label: "Vibrant" },
  { value: "Intimate",    label: "Intimate" },
  { value: "Glamorous",   label: "Glamorous" },
  { value: "Corporate",   label: "Corporate" },
];

const CITY_FILTERS = [
  { value: "",            label: "Any City" },
  { value: "Hyderabad",   label: "Hyderabad" },
  { value: "Delhi",       label: "Delhi" },
  { value: "Mumbai",      label: "Mumbai" },
  { value: "Bengaluru",   label: "Bengaluru" },
  { value: "Udaipur",     label: "Udaipur" },
];

// ── Typography helpers ────────────────────────────────────────────────────────

const serif  = (extra = {}) => ({ fontFamily: "'Cormorant Garamond', Georgia, serif", ...extra });
const sans   = (extra = {}) => ({ fontFamily: "'DM Sans', sans-serif", ...extra });

// ── Nav ───────────────────────────────────────────────────────────────────────

function PublicNav() {
  const nav = useNavigate();
  return (
    <nav style={{ background: PJ.obsidian, position: "sticky", top: 0, zIndex: 100,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "0 clamp(16px,4vw,56px)", height: 60, borderBottom: `1px solid rgba(255,255,255,0.06)` }}
         data-testid="explore-nav">
      {/* Logo */}
      <button onClick={() => nav("/")} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
        <img
          src="/prashasta-logo.png"
          alt="Prashasta Events"
          data-testid="explore-nav-logo"
          style={{ height: 28, width: "auto", objectFit: "contain" }}
          onError={e => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
        />
        <div style={{ display: "none", width: 28, height: 28, borderRadius: "50%", background: PJ.ruby, alignItems: "center", justifyContent: "center" }}>
          <span style={serif({ color: PJ.ivory, fontSize: 14, fontWeight: 600 })}>P</span>
        </div>
        <span style={serif({ color: PJ.ivory, fontSize: 18, fontWeight: 500, letterSpacing: "0.02em" })}>
          Prashasta
        </span>
      </button>

      {/* Nav links */}
      <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
        <NavLink label="Explore" active onClick={() => nav("/explore")} />
        <span className="pj-nav-find-style">
          <NavLink label="Find My Style" onClick={() => nav("/find-events-like-mine")} />
        </span>        <button
          data-testid="nav-build-my-event"
          onClick={() => nav("/build-my-event")}
          style={{ ...sans({ fontSize: 13, fontWeight: 600, color: PJ.white }),
                   background: PJ.ruby, border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer" }}>
          Build My Event
        </button>
      </div>
    </nav>
  );
}

function NavLink({ label, active, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ background: "none", border: "none", cursor: "pointer",
               ...sans({ fontSize: 13, fontWeight: active ? 600 : 400,
                         color: active ? PJ.ivory : hov ? PJ.ivory : PJ.slate,
                         transition: "color 0.15s", padding: "4px 0",
                         borderBottom: active ? `1.5px solid ${PJ.gold}` : "1.5px solid transparent" }) }}>
      {label}
    </button>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────

function HeroSection({ filters, setFilters }) {
  const nav = useNavigate();

  return (
    <section style={{ background: PJ.obsidian, padding: "clamp(48px,8vw,96px) clamp(16px,4vw,56px) 0" }}
             data-testid="explore-hero">
      {/* Eyebrow */}
      <p style={sans({ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase",
                        color: PJ.gold, marginBottom: 16 })}>
        Prashasta — Real Events
      </p>

      {/* Headline */}
      <h1 style={serif({ fontSize: "clamp(2.4rem, 6vw, 5rem)", fontWeight: 500, color: PJ.ivory,
                          lineHeight: 1.1, marginBottom: 20, maxWidth: 640 })}
          data-testid="explore-headline">
        Explore Real Events
      </h1>

      {/* Subtext */}
      <p style={sans({ fontSize: "clamp(0.9rem, 1.5vw, 1.05rem)", color: PJ.slate, maxWidth: 520,
                        lineHeight: 1.65, marginBottom: 40 })}>
        Every event below was planned and delivered by Prashasta.
        Browse by type, scale, or style — then tell us what you have in mind.
      </p>

      {/* Three entry points */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 40 }}>
        <EntryTab label="Browse Ideas" active icon="◎" />
        <EntryTab label="Find My Style" icon="◇" onClick={() => nav("/find-events-like-mine")} />
        <EntryTab label="Build My Event" icon="→" onClick={() => nav("/build-my-event")} />
      </div>

      {/* Filter bar — lives at bottom of hero, transitions to gallery */}
      <FilterBar filters={filters} setFilters={setFilters} />
    </section>
  );
}

function EntryTab({ label, active, icon, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      data-testid={`entry-tab-${label.toLowerCase().replace(/ /g, "-")}`}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ background: active ? `rgba(255,255,255,0.08)` : hov ? `rgba(255,255,255,0.05)` : "transparent",
               border: active ? `1px solid rgba(255,255,255,0.18)` : `1px solid rgba(255,255,255,0.08)`,
               borderRadius: 6, padding: "8px 16px", cursor: onClick ? "pointer" : "default",
               display: "flex", alignItems: "center", gap: 7,
               transition: "all 0.15s" }}>
      <span style={{ color: active ? PJ.gold : PJ.slate, fontSize: 12 }}>{icon}</span>
      <span style={sans({ fontSize: 13, fontWeight: 500,
                           color: active ? PJ.ivory : PJ.slate })}>{label}</span>
    </button>
  );
}

// ── Filter bar ────────────────────────────────────────────────────────────────

function FilterBar({ filters, setFilters }) {
  const pill = (active) => ({
    ...sans({ fontSize: 12, fontWeight: 500, whiteSpace: "nowrap" }),
    padding: "6px 14px", borderRadius: 20, cursor: "pointer", border: "1px solid",
    background: active ? PJ.gold : "transparent",
    color: active ? PJ.obsidian : PJ.slate,
    borderColor: active ? PJ.gold : `rgba(255,255,255,0.15)`,
    transition: "all 0.12s",
  });

  const Section = ({ label, options, field }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={sans({ fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase",
                           color: PJ.slate, whiteSpace: "nowrap" })}>{label}</span>
      <div style={{ display: "flex", gap: 5, overflowX: "auto", paddingBottom: 2, scrollbarWidth: "none" }}>
        {options.map(o => (
          <button key={o.value} style={pill(filters[field] === o.value)}
                  data-testid={`filter-${field}-${o.value || "all"}`}
                  onClick={() => setFilters(f => ({ ...f, [field]: f[field] === o.value ? "" : o.value }))}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="explore-filter-bar"
         style={{ borderTop: `1px solid rgba(255,255,255,0.06)`, paddingTop: 20, paddingBottom: 24,
                  display: "flex", flexDirection: "column", gap: 12, overflowX: "auto" }}>
      <Section label="Type"  options={EVENT_TYPE_FILTERS}  field="eventType" />
      <Section label="Scale" options={GUEST_SCALE_FILTERS} field="guestRange" />
      <Section label="Style" options={STYLE_FILTERS}       field="style" />
      <Section label="City"  options={CITY_FILTERS}        field="city" />
    </div>
  );
}

// ── Event card ────────────────────────────────────────────────────────────────

function EventCard({ event, size = "normal" }) {
  const nav = useNavigate();
  const [hov, setHov] = useState(false);
  const [planning, setPlanning] = useState(false);
  const imgH = size === "large" ? 320 : 240;

  const typeLabel = {
    WEDDING: "Wedding", SANGEET: "Sangeet", ENGAGEMENT: "Engagement",
    BIRTHDAY: "Birthday", CORPORATE: "Corporate", CONFERENCE: "Conference", AWARDS: "Awards",
  }[event.event_type] || event.event_type;

  // EXP-01G: Add EXPLORE_GALLERY path entry when navigating to a story from the gallery.
  const handleViewStory = useCallback(() => {
    const existing = loadJourneyFromStorage();
    const sessionId = existing?.journey_session_id || crypto.randomUUID();
    const base = existing || { journey_session_id: sessionId };
    saveJourneyToStorage({
      ...base,
      journey_session_id: sessionId,
      journey_path: addJourneyPathEntry(base, 'EXPLORE_GALLERY'),
    });
    nav(`/explore/${event.slug}`);
  }, [event.slug, nav]);

  // EXP-01G: Gallery card "Plan Like This" — full attribution + field pre-fill.
  const handlePlanLikeThis = useCallback(async () => {
    setPlanning(true);
    try {
      const res = await fetch(`${API}/api/public/inspirations/${event.slug}/transferable`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const allSuggested = mapInspirationToJourneyFields(data.fields || {});
      const existing = loadJourneyFromStorage();
      const sessionId = existing?.journey_session_id || crypto.randomUUID();

      // Never override a field already confirmed by the customer
      const fieldsToApply = {};
      Object.entries(allSuggested).forEach(([k, v]) => {
        if (existing?.[k]?.confirmed !== true) fieldsToApply[k] = v;
      });

      // Inspiration-origin attribution: only set if no prior source was captured
      const attribution = existing?.source_captured ? {} : {
        source_channel:    'WEBSITE',
        source_touchpoint: 'INSPIRATION_GALLERY',
        source_entry_point:'PLAN_ONE_LIKE_THIS',
        source_captured:   true,
        source_first_seen: new Date().toISOString(),
      };

      // Path: EXPLORE_GALLERY was added by handleViewStory if they visited a story first.
      // From the gallery card, we add PLAN_ONE_LIKE_THIS directly.
      const base = existing || { journey_session_id: sessionId };
      const pathWithPlan = addJourneyPathEntry(base, 'PLAN_ONE_LIKE_THIS');

      saveJourneyToStorage({
        ...(existing || {}),
        journey_session_id: sessionId,
        ...attribution,
        journey_path: pathWithPlan,
        ...fieldsToApply,
        inspiration: {
          selected:            true,
          selected_match_slug: event.slug,
          inspiration_title:   data.inspiration_title || event.title,
          source:              'PORTFOLIO',
          selected_at:         new Date().toISOString(),
          suggested_field_keys: Object.keys(fieldsToApply),
        },
        event_match: { ...(existing?.event_match || {}), selected_match_slug: event.slug },
      });

      nav('/build-my-event');
    } catch (err) {
      console.error('EXP-01G: gallery handlePlanLikeThis failed — navigating to BME without context', err);
      nav('/build-my-event');
    } finally {
      setPlanning(false);
    }
  }, [event.slug, event.title, nav]);

  return (
    <article
      data-testid={`event-card-${event.slug}`}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ background: "#FFFFFF", borderRadius: 12, overflow: "hidden",
               boxShadow: hov ? "0 12px 40px rgba(0,0,0,0.14)" : "0 2px 12px rgba(0,0,0,0.06)",
               transition: "box-shadow 0.2s, transform 0.2s",
               transform: hov ? "translateY(-2px)" : "none",
               cursor: "pointer" }}>

      {/* Image — navigates to story page (EXP-01C) */}
      <div style={{ position: "relative", height: imgH, overflow: "hidden" }}
           onClick={handleViewStory}>
        <img
          src={event.hero_image_url}
          alt={event.title}
          loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "cover",
                   objectPosition: "50% 25%",
                   transition: "transform 0.4s",
                   transform: hov ? "scale(1.04)" : "scale(1)" }} />
        {/* Gradient overlay */}
        <div style={{ position: "absolute", inset: 0,
                      background: "linear-gradient(to top, rgba(32,27,25,0.75) 0%, transparent 50%)",
                      pointerEvents: "none" }} />
        {/* Bottom chips */}
        <div style={{ position: "absolute", bottom: 12, left: 12, display: "flex", gap: 5, flexWrap: "wrap" }}>
          <TypeChip label={typeLabel} />
          <CityChip label={event.location_city} />
          {event.is_featured && <FeaturedChip />}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "16px 18px 14px" }}>
        <h3 style={serif({ fontSize: "1.1rem", fontWeight: 500, color: PJ.obsidian,
                            marginBottom: 4, lineHeight: 1.25,
                            cursor: "pointer" })}
            onClick={handleViewStory}>
          {event.title}
        </h3>
        <p style={sans({ fontSize: "0.78rem", color: PJ.slate, marginBottom: 12 })}>
          {event.style_direction}
        </p>

        {/* CTAs — EXP-01G: both buttons fully live */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            data-testid={`view-story-${event.slug}`}
            onClick={e => { e.stopPropagation(); handleViewStory(); }}
            style={{ flex: 1, ...sans({ fontSize: 12, fontWeight: 600, color: PJ.white }),
                     background: PJ.obsidian, border: "none", borderRadius: 6,
                     padding: "8px 0", cursor: "pointer",
                     transition: "background 0.12s" }}
            onMouseEnter={e => e.currentTarget.style.background = "#3A3330"}
            onMouseLeave={e => e.currentTarget.style.background = PJ.obsidian}>
            View Story
          </button>
          <button
            data-testid={`plan-like-this-${event.slug}`}
            disabled={planning}
            onClick={e => { e.stopPropagation(); handlePlanLikeThis(); }}
            style={{ flex: 1, ...sans({ fontSize: 12, fontWeight: 500,
                     color: planning ? PJ.slate : PJ.obsidian }),
                     background: "transparent", border: `1.5px solid ${planning ? PJ.sand : PJ.obsidian}`,
                     borderRadius: 6, padding: "8px 0",
                     cursor: planning ? "not-allowed" : "pointer",
                     transition: "border-color 0.12s, color 0.12s" }}
            onMouseEnter={e => { if (!planning) { e.currentTarget.style.borderColor = PJ.gold; e.currentTarget.style.color = PJ.gold; }}}
            onMouseLeave={e => { if (!planning) { e.currentTarget.style.borderColor = PJ.obsidian; e.currentTarget.style.color = PJ.obsidian; }}}>
            {planning ? "Preparing…" : "Plan Like This"}
          </button>
        </div>
      </div>
    </article>
  );
}

function TypeChip({ label }) {
  return (
    <span style={{ ...sans({ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }),
                   background: "rgba(32,27,25,0.72)", color: PJ.ivory, backdropFilter: "blur(8px)",
                   padding: "3px 8px", borderRadius: 4 }}>
      {label}
    </span>
  );
}

function CityChip({ label }) {
  return (
    <span style={{ ...sans({ fontSize: 10, fontWeight: 500 }),
                   background: "rgba(32,27,25,0.52)", color: PJ.sand, backdropFilter: "blur(8px)",
                   padding: "3px 8px", borderRadius: 4 }}>
      {label}
    </span>
  );
}

function FeaturedChip() {
  return (
    <span style={{ ...sans({ fontSize: 10, fontWeight: 600, letterSpacing: "0.04em" }),
                   background: PJ.gold, color: PJ.obsidian, padding: "3px 8px", borderRadius: 4 }}>
      Featured
    </span>
  );
}

// ── Featured section ───────────────────────────────────────────────────────────

function FeaturedSection({ events }) {
  if (!events.length) return null;
  return (
    <section data-testid="explore-featured-section"
             style={{ padding: "48px clamp(16px,4vw,56px) 32px", background: PJ.ivory }}>
      <SectionHeader label="Featured Events" count={events.length} />
      {/* Desktop: 2-column grid | Mobile: horizontal scroll */}
      <div style={{ display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 480px), 1fr))",
                    gap: 24 }}>
        {events.map(ev => (
          <EventCard key={ev.slug} event={ev} size="large" />
        ))}
      </div>
    </section>
  );
}

// ── All events grid ────────────────────────────────────────────────────────────

function AllEventsGrid({ events, loading }) {
  if (loading) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
        {[...Array(6)].map((_, i) => (
          <div key={i} style={{ height: 340, borderRadius: 12,
                                background: "linear-gradient(90deg, #F0ECE8 25%, #FAF7F4 50%, #F0ECE8 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.4s infinite linear" }} />
        ))}
      </div>
    );
  }

  if (!events.length) {
    return (
      <div data-testid="explore-no-results"
           style={{ textAlign: "center", padding: "64px 24px" }}>
        <p style={serif({ fontSize: "1.4rem", color: PJ.slate, marginBottom: 8 })}>
          No events match these filters
        </p>
        <p style={sans({ fontSize: "0.85rem", color: PJ.slate })}>
          Try removing a filter to see more events.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="explore-events-grid"
         style={{ display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: 20 }}>
      {events.map(ev => (
        <EventCard key={ev.slug} event={ev} />
      ))}
    </div>
  );
}

function SectionHeader({ label, count, subtle }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 24 }}>
      <h2 style={serif({ fontSize: "1.6rem", fontWeight: 500, color: subtle ? PJ.slate : PJ.obsidian, margin: 0 })}>
        {label}
      </h2>
      {count !== undefined && (
        <span style={sans({ fontSize: "0.78rem", color: PJ.slate })}>{count} events</span>
      )}
    </div>
  );
}

// ── CTA band ──────────────────────────────────────────────────────────────────

function CTABand() {
  const nav = useNavigate();
  return (
    <section data-testid="explore-cta-band"
             style={{ background: PJ.obsidian, padding: "56px clamp(16px,4vw,56px)",
                      textAlign: "center" }}>
      <p style={sans({ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.18em",
                        textTransform: "uppercase", color: PJ.gold, marginBottom: 12 })}>
        Not sure where to start?
      </p>
      <h2 style={serif({ fontSize: "clamp(1.8rem,4vw,3rem)", fontWeight: 500, color: PJ.ivory,
                          marginBottom: 12, lineHeight: 1.15 })}>
        Find Events Like Mine
      </h2>
      <p style={sans({ fontSize: "0.9rem", color: PJ.slate, marginBottom: 28, maxWidth: 420, margin: "0 auto 28px" })}>
        Answer a few questions about your event and we will show you the most relevant experiences from our portfolio.
      </p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <button
          data-testid="cta-find-events"
          onClick={() => nav("/find-events-like-mine")}
          style={{ ...sans({ fontSize: 14, fontWeight: 600, color: PJ.white }),
                   background: PJ.ruby, border: "none", borderRadius: 8,
                   padding: "12px 28px", cursor: "pointer" }}>
          Find My Match
        </button>
        <button
          data-testid="cta-build-event"
          onClick={() => nav("/build-my-event")}
          style={{ ...sans({ fontSize: 14, fontWeight: 600, color: PJ.ivory }),
                   background: "transparent", border: `1.5px solid rgba(255,255,255,0.25)`,
                   borderRadius: 8, padding: "12px 28px", cursor: "pointer" }}>
          Build My Event
        </button>
      </div>
    </section>
  );
}

// ── Shimmer keyframe ──────────────────────────────────────────────────────────

const shimmerStyle = `
  @keyframes shimmer {
    from { background-position: 200% 0; }
    to   { background-position: -200% 0; }
  }
  ::-webkit-scrollbar { display: none; }
  @media (max-width: 600px) {
    [data-testid="explore-nav"] .pj-nav-find-style { display: none; }
  }
`;

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ExplorePage() {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ eventType: "", guestRange: "", style: "", city: "" });

  useEffect(() => {
    async function load() {
      try {
        const r = await fetch(`${API}/api/public/inspirations?limit=50`);
        if (!r.ok) throw new Error("fetch failed");
        const data = await r.json();
        setEvents(data.items || []);
      } catch (e) {
        console.error("Explore: failed to load events", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const featuredEvents = useMemo(() => events.filter(e => e.is_featured), [events]);

  const filteredEvents = useMemo(() => {
    return events.filter(e => {
      if (filters.eventType && e.event_type !== filters.eventType) return false;
      if (filters.guestRange && e.guest_range !== filters.guestRange) return false;
      if (filters.style && !e.style_tags?.some(t => t.toLowerCase() === filters.style.toLowerCase())) return false;
      if (filters.city && !e.location_city?.toLowerCase().includes(filters.city.toLowerCase())) return false;
      return true;
    });
  }, [events, filters]);

  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <>
      <style>{shimmerStyle}</style>
      <div style={{ minHeight: "100vh", background: PJ.ivory }} data-testid="explore-page">
        <PublicNav />
        <HeroSection filters={filters} setFilters={setFilters} />

        {/* Gallery canvas — warm ivory */}
        <div style={{ background: PJ.ivory }}>
          {/* Featured — shown only when no type/style filter active */}
          {!hasFilters && <FeaturedSection events={featuredEvents} />}

          {/* All events grid */}
          <section style={{ padding: `${hasFilters ? "48px" : "8px"} clamp(16px,4vw,56px) 56px`,
                            background: PJ.ivory }}>
            {hasFilters
              ? <SectionHeader label="Matching Events" count={filteredEvents.length} />
              : <SectionHeader label="All Events" count={events.length} />}
            <AllEventsGrid events={hasFilters ? filteredEvents : events} loading={loading} />
          </section>
        </div>

        {/* Dark CTA band */}
        <CTABand />
      </div>
    </>
  );
}
